import { Vector3, MathUtils, Vector2, Raycaster, Matrix4 } from 'three';
import { commonConstants, distancePenaltyConstants } from '../const_params';
import { lin_mult, getCamToUiTx } from '../utils/threeUtils';
import { projectPointToImage } from '../lumina.common.logic';

const view_side_types = {
  lingual: 0,
  ling_occl: 1,
  occlusal: 2,
  bucc_occl: 3,
  buccal: 4,
};

const {
  caries_detection_score_threshold,
  max_dist_from_cam_to_img_cen_on_surf,
  angie_max_distance_between_pr_pt_and_cam_mm,
  max_distance_between_image_and_view_projections,
  checkLoupeVisibility,
  rotation_angle_compliance_coefficient,
  use_rotation_angle,
  use_adaptive_angle,
  front_occlusal_separator_deg,
  back_occlusal_separator_deg,
  adaptive_angle_penalty_coefficient,
} = commonConstants;

const {
  distance_compliance_coefficient,
  angle_compliance_coefficient,
  caries_detection_compliance_coefficient,
  bad_image_height_caries_score,
  bad_height_penalty,
  bad_quality_penalty,
  bad_image_quality_caries_score,
  min_acceptable_caries_score,
} = distancePenaltyConstants;

const isFrontFace = (intersect, raycaster) => {
  const { face, object } = intersect;
  const normal = face.normal
    .clone()
    .applyMatrix4(object.matrixWorld)
    .normalize();
  const rayDirection = raycaster.ray.direction;
  const isFrontFace = normal.dot(rayDirection) < 0;
  return isFrontFace;
};

const project = (origin_point, imageViewDirection, mesh) => {
  const rayCaster = new Raycaster(origin_point, imageViewDirection.normalize(), 0, Infinity);
  rayCaster.firstHitOnly = true;
  const intersects = rayCaster.intersectObjects([mesh]);
  return intersects.reduce((acc, intersect) => {
    if (intersect) {
      intersect.isFrontFace = isFrontFace(intersect, rayCaster);
    }
    return acc;
  }, intersects);
};

const checkLoupeVisibilityForImage = (cam_to_abs_tx, intersect_pt, camera_pt, mesh) => {
  const abs_to_cam_tx = cam_to_abs_tx.clone().invert();
  const pointInProjectionCs = intersect_pt.clone().applyMatrix4(abs_to_cam_tx);
  // If a point is behind the view, it's not visible
  if (pointInProjectionCs.z < 0) {
    return false;
  }

  const origin_point = camera_pt.clone();
  const perspective_view_direction = new Vector3().subVectors(intersect_pt, origin_point).normalize();

  const intersects = project(origin_point, perspective_view_direction, mesh);
  if (intersects.length === 0) {
    return true;
  }

  const { isFrontFace } = intersects[0];
  const distanceBetweenImageAndViewProjections = new Vector3().subVectors(intersects[0].point, intersect_pt).length();

  return isFrontFace && distanceBetweenImageAndViewProjections < max_distance_between_image_and_view_projections;
};

const piecewise_penalty_function = (x) => {
  const wellSize = 0.25;
  const linearSlope = 2;
  const linearIntercept = -linearSlope * wellSize + wellSize ** 4;

  if (x < wellSize) {
    return x ** 4;
  } else {
    return linearSlope * x + linearIntercept;
  }
};

const calculate_rotation_angles = (image_to_view) => {
  const image_cs_projected_x_basis_vector = new Vector2(
    -image_to_view.elements[0],
    -image_to_view.elements[1]
  ).normalize();

  const acos = Math.acos(image_cs_projected_x_basis_vector.x);
  const sign = image_cs_projected_x_basis_vector.y > 0 ? -1 : 1;
  const rotation_angle = Math.sign(sign) * Math.abs(acos);

  return 1 - (Math.cos(4 * rotation_angle) + 1) / 2;
};

const get_view_side = (direction, occlusal_separator_z, semi_occlusal_separator_z, intersect_pt, jaws_center) => {
  const z = Math.abs(direction.normalize().z);
  const radial_separator = 0.3;

  if (z > occlusal_separator_z) {
    return view_side_types.occlusal;
  }

  const dot = new Vector2(intersect_pt.x - jaws_center.x, intersect_pt.y)
    .normalize()
    .dot(new Vector2(direction.x, direction.y).normalize());
  if (Math.abs(dot) < radial_separator) {
    return view_side_types.occlusal;
  }

  const semi_occlusal = z > semi_occlusal_separator_z;

  if (dot > 0) {
    return semi_occlusal ? view_side_types.ling_occl : view_side_types.lingual;
  }

  return semi_occlusal ? view_side_types.bucc_occl : view_side_types.buccal;
};

const check_elliptical_roi = (roi, roi_scaling_coff, projected_point_on_image) => {
  if (roi.is_empty()) return false;

  const scaling_coefficient_for_circle_roi = 0.8;
  const anizotropic =
    1 + (Math.max(roi_scaling_coff, scaling_coefficient_for_circle_roi) - scaling_coefficient_for_circle_roi) / 2;
  const x_axis = Math.min(roi.x_size(), roi.y_size()) / 2;
  const y_axis = x_axis * anizotropic;
  const center = roi.center();

  roi.x_min = center.x - x_axis;
  roi.x_max = center.x + x_axis;
  roi.y_min = center.y - y_axis;
  roi.y_max = center.y + y_axis;

  const scale = roi_scaling_coff;
  roi.inflate(scale);

  const dx = (projected_point_on_image.x - center.x) / (roi.x_size() / 2);
  const dy = (projected_point_on_image.y - center.y) / (roi.y_size() / 2);

  return dx ** 2 + dy ** 2 <= 1;
};

const calculate_adaptive_angles = (
  intersect_pt,
  surface_bbox,
  front_occlusal_separator_deg,
  back_occlusal_separator_deg,
  image_camera_direction,
  view_camera_direction
) => {
  const occlusal_separator_deg =
    back_occlusal_separator_deg -
    ((back_occlusal_separator_deg - front_occlusal_separator_deg) * (intersect_pt.x - surface_bbox.min.x)) /
      (surface_bbox.max.x - surface_bbox.min.x);

  const semi_occlusal_separator_deg = 10;

  const occlusal_separator_z = Math.cos(MathUtils.degToRad(occlusal_separator_deg));
  const semi_occlusal_separator_z = Math.cos(MathUtils.degToRad(occlusal_separator_deg + semi_occlusal_separator_deg));
  const jaws_center = new Vector2(surface_bbox.min.x + (surface_bbox.max.x - surface_bbox.min.x) / 4, 0);

  const image_view_side = get_view_side(
    image_camera_direction,
    occlusal_separator_z,
    semi_occlusal_separator_z,
    intersect_pt,
    jaws_center
  );
  const camera_view_side = get_view_side(
    view_camera_direction,
    occlusal_separator_z,
    semi_occlusal_separator_z,
    intersect_pt,
    jaws_center
  );

  const zFactor = occlusal_separator_z - Math.abs(view_camera_direction.z);

  if (zFactor < 0) {
    const occlusal_constriction = 2;
    image_camera_direction.x *= 1 - zFactor * occlusal_constriction;
    image_camera_direction.y *= 1 - zFactor * occlusal_constriction;
  } else {
    const view_direction_xy = new Vector3(view_camera_direction.x, view_camera_direction.y, 0).normalize();
    const buccal_tx = new Matrix4().set(
      view_direction_xy.x,
      view_direction_xy.y,
      0,
      0,
      view_direction_xy.y,
      -view_direction_xy.x,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1
    );

    image_camera_direction = lin_mult(image_camera_direction, buccal_tx);
    view_camera_direction = lin_mult(view_camera_direction, buccal_tx);

    const buccal_stretching = 1.5;
    const occlusal_constriction = 0;

    image_camera_direction.z *= 1 + zFactor * occlusal_constriction;
    image_camera_direction.y /= 1 + zFactor * buccal_stretching;
  }

  image_camera_direction.normalize();

  const suitable_view_side = [[1, 1, 0, 0, 0], [1, 1, 1, 1, 0], [0, 1, 1, 1, 0], [0, 1, 1, 1, 1], [0, 0, 0, 1, 1]];

  return {
    suitable_view_side: suitable_view_side[camera_view_side][image_view_side],
    image_camera_direction,
    view_camera_direction,
  };
};

export const computePositionValueAndDirection = ({
  camera,
  meshes,
  jawName,
  image_idx,
  intersect,
  view_direction,
  images_meta_data,
  max_angle_difference,
  roiInflateRate,
}) => {
  const intersect_pt = intersect.point;
  const currentImageData = images_meta_data[image_idx];

  if (!currentImageData) return;

  const {
    scan_role,
    was_cam_projected,
    img_cen_on_surf_pt,
    camera_pt,
    camera_dir,
    rect_of_image,
    caries_detection_score,
    camera_to_pixel,
    K_vector,
    P_vector,
    cam_to_abs_tx,
    caries_reference_points,
    worldToCamTx,
    surface_bbox,
  } = currentImageData;

  if (caries_detection_score < min_acceptable_caries_score) return;

  if (scan_role !== jawName) return;
  // 1. Constraint: the distance between loupe position and image center projected on surface should not differ too much.
  // Although similar criteria is utilised below, they rely on the fact that projected image center is actually visible from
  // corresponding camera. However, it might happen that image center on surface is not visible from camera, but belongs to the
  // region of interest. This is due to the fact the we do not apply rasterization/ray tracing, while projection algorithm
  if (!was_cam_projected) return;

  const distance_between_image_projection_and_selected_point_mm = new Vector3()
    .subVectors(intersect_pt, img_cen_on_surf_pt)
    .length();
  if (distance_between_image_projection_and_selected_point_mm > max_dist_from_cam_to_img_cen_on_surf) return;

  if (rect_of_image.is_empty()) return;

  // 2. Constraint: loupe should actually belong to the region of interest
  const projected2DPointOnImage = projectPointToImage(intersect, worldToCamTx, camera_to_pixel, K_vector, P_vector);
  const cloned_rect_of_image = rect_of_image.clone();
  const isInRoiBounds = check_elliptical_roi(cloned_rect_of_image, roiInflateRate, projected2DPointOnImage);
  if (!isInRoiBounds) return;

  // 3. Constraint: the distance between loupe position and image center projected on surface should not differ too much.
  const distance_between_camera_and_selected_point_mm = new Vector3().subVectors(intersect_pt, camera_pt).length();
  if (distance_between_camera_and_selected_point_mm > angie_max_distance_between_pr_pt_and_cam_mm) return;

  // 4. Constraint capture height: selected image shouldn't be too close to the surface
  if (caries_detection_score === bad_image_height_caries_score) return;

  // 5. Constraint Angle between view direction and camera direction should not differ too much
  const image_camera_direction = new Vector3().copy(camera_dir);
  const view_camera_direction = new Vector3().copy(view_direction).normalize();

  let adaptive_angle_penalty = 0;

  if (use_adaptive_angle) {
    const calculate_adaptive_angles_res = calculate_adaptive_angles(
      intersect_pt,
      surface_bbox,
      front_occlusal_separator_deg,
      back_occlusal_separator_deg,
      image_camera_direction,
      view_camera_direction
    );
    image_camera_direction.set(
      calculate_adaptive_angles_res.image_camera_direction.x,
      calculate_adaptive_angles_res.image_camera_direction.y,
      calculate_adaptive_angles_res.image_camera_direction.z
    );
    view_camera_direction.set(
      calculate_adaptive_angles_res.view_camera_direction.x,
      calculate_adaptive_angles_res.view_camera_direction.y,
      calculate_adaptive_angles_res.view_camera_direction.z
    );
    if (!calculate_adaptive_angles_res.suitable_view_side) {
      adaptive_angle_penalty = adaptive_angle_penalty_coefficient;
    }
  }

  const angle_diff = image_camera_direction.angleTo(view_camera_direction);
  if (angle_diff > MathUtils.degToRad(max_angle_difference)) return;

  // 6. Constraint Loupe should display only physically visible images
  if (checkLoupeVisibility) {
    const mesh = meshes.find((mesh) => mesh.name === jawName);
    if (mesh) {
      const isLoupeVisibleOnSurface = checkLoupeVisibilityForImage(
        cam_to_abs_tx,
        intersect_pt,
        camera_pt,
        meshes.find((mesh) => mesh.name === jawName)
      );

      if (!isLoupeVisibleOnSurface) return;
    }
  }

  // distance
  const roi_center_pt = cloned_rect_of_image.center();
  const distance_px = new Vector2().subVectors(roi_center_pt, projected2DPointOnImage).length();

  const distance_compliance =
    distance_px / new Vector2().subVectors(cloned_rect_of_image.corner_max(), cloned_rect_of_image.center()).length();
  const distance_penalty = piecewise_penalty_function(distance_compliance) * distance_compliance_coefficient;

  // angle
  const angle_compliance = angle_diff / MathUtils.degToRad(max_angle_difference);
  const angle_penalty = piecewise_penalty_function(angle_compliance) * angle_compliance_coefficient;

  // adds rotation angle to the penalty function to favor images aligned with the 3D model's view, improving visual consistency.
  const cam_to_ui_tx = getCamToUiTx(camera.matrixWorldInverse, cam_to_abs_tx);
  const rotation_angle_compliance = use_rotation_angle ? calculate_rotation_angles(cam_to_ui_tx) : 0;
  const rotation_angle_penalty = rotation_angle_compliance * rotation_angle_compliance_coefficient;

  // caries
  let caries_score = caries_detection_score || 0;
  const caries_detection_score_is_invalid = caries_score === bad_image_quality_caries_score;

  if (!caries_detection_score_is_invalid && caries_reference_points.length > 0) {
    const { score } = caries_reference_points.reduce(
      (acc, { point, score }) => {
        const dist = new Vector3().subVectors(point, intersect_pt).length();
        return dist < acc.dist ? { dist, score } : acc;
      },
      { dist: Number.MAX_VALUE, score: caries_score }
    );
    caries_score = score;
  }

  let compliance_caries_score = caries_score;
  if (caries_score < caries_detection_score_threshold) {
    compliance_caries_score = 0; // If caries detection score is below threshold, we consider it as invalid.
  }

  const caries_detection_compliance = 1 - compliance_caries_score;
  let caries_detection_penalty = 0;

  if (caries_score <= bad_image_quality_caries_score) {
    caries_detection_penalty = bad_quality_penalty;
  } else if (caries_score <= bad_image_height_caries_score) {
    caries_detection_penalty = bad_height_penalty;
  } else {
    caries_detection_penalty = caries_detection_compliance * caries_detection_compliance_coefficient;
  }

  const penalty_value =
    distance_penalty + angle_penalty + caries_detection_penalty + rotation_angle_penalty + adaptive_angle_penalty;

  return {
    img_idx: image_idx,
    penalty: penalty_value,
    selected2DPointOnImage: projected2DPointOnImage,
  };
};
